import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Divider,
  List,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { demoRegisterExpeditorSchema } from '@expeditor/shared';
import { authApi } from '../../shared/api/auth';
import { fetchDemoMode } from '../../shared/demo/useDemoMode';

/**
 * Демо-страница входа.
 *
 * Единственная точка входа на демо-инстансе (DEMO_MODE=1): объясняет порядок
 * работы и даёт само-регистрацию «демо-сотрудника» (MANAGER) на реальный email.
 * На прод-инстансе (демо выключен) показывает мягкую заглушку со ссылкой на вход.
 */
export function DemoPage() {
  const [demoMode, setDemoMode] = useState<boolean | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devVerificationUrl, setDevVerificationUrl] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  useEffect(() => {
    void fetchDemoMode().then(setDemoMode);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDevVerificationUrl(null);
    const parsed = demoRegisterExpeditorSchema.safeParse({
      email,
      password,
      fullName,
      phone: phone || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join('; '));
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.registerDemoExpeditor(parsed.data);
      setRegisteredEmail(email);
      if (res.devVerification?.url) setDevVerificationUrl(res.devVerification.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось зарегистрироваться');
    } finally {
      setSubmitting(false);
    }
  };

  if (demoMode === false) {
    return (
      <Container size="xs" py="xl">
        <Alert color="gray" title="Демо-режим недоступен">
          <Text size="sm">
            Это рабочий инстанс системы.{' '}
            <Anchor component={Link} to="/login">
              Перейти ко входу
            </Anchor>
            .
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <Stack gap="xs" align="center">
          <Badge size="lg" variant="light" color="orange">
            Демо-стенд
          </Badge>
          <Title order={2} ta="center">
            Попробуйте систему экспедитора
          </Title>
          <Text c="dimmed" ta="center" maw={560}>
            Открытый демо-стенд: зарегистрируйтесь как сотрудник-экспедитор и
            посмотрите работу и со стороны экспедитора, и со стороны клиента.
            Это изолированная площадка — реальные данные здесь не хранятся.
          </Text>
        </Stack>

        <Paper withBorder shadow="sm" p="lg" radius="md">
          <Title order={4} mb="sm">
            Порядок работы
          </Title>
          <List type="ordered" spacing="xs" size="sm" center>
            <List.Item>
              Зарегистрируйтесь ниже как <strong>демо-сотрудник</strong> — на
              реальный email (нужен для подтверждения).
            </List.Item>
            <List.Item>
              Перейдите по ссылке из письма, чтобы подтвердить адрес.
            </List.Item>
            <List.Item>
              Войдите — вы попадёте в кабинет экспедитора. Там уже есть
              витринные клиенты, поручения, перевозки и документы — их можно
              открывать и изучать.
            </List.Item>
            <List.Item>
              Чтобы увидеть систему глазами заказчика, в кабинете экспедитора
              откройте пункт меню <strong>«Создать демо-клиента»</strong> и
              задайте клиенту email и пароль.
            </List.Item>
            <List.Item>
              Откройте второе окно браузера (или режим инкогнито) и войдите под
              этим клиентом — так вы попробуете сторону клиента, не выходя из
              кабинета экспедитора.
            </List.Item>
            <List.Item>
              Клиенты, которых вы создали, и их данные видны только вам.
              Витринные примеры — общие для всех посетителей.
            </List.Item>
          </List>
        </Paper>

        <Paper withBorder shadow="sm" p="lg" radius="md">
          <Title order={4} mb="sm">
            Регистрация демо-сотрудника
          </Title>

          {registeredEmail ? (
            <Stack>
              <Alert
                color={devVerificationUrl ? 'yellow' : 'blue'}
                title="Почти готово — подтвердите email"
              >
                <Text size="sm">
                  {devVerificationUrl ? (
                    <>
                      Демо-режим: письмо на <strong>{registeredEmail}</strong>{' '}
                      могло не прийти (почта на стенде не обязательна) — просто
                      перейдите по ссылке подтверждения:
                    </>
                  ) : (
                    <>
                      Письмо со ссылкой подтверждения отправлено на{' '}
                      <strong>{registeredEmail}</strong>. Перейдите по ссылке из
                      письма, затем войдите.
                    </>
                  )}
                </Text>
                {devVerificationUrl && (
                  <Anchor href={devVerificationUrl} target="_blank" mt={6}>
                    {devVerificationUrl}
                  </Anchor>
                )}
              </Alert>
              <Button component={Link} to="/login">
                Перейти ко входу
              </Button>
            </Stack>
          ) : (
            <form onSubmit={onSubmit}>
              <Stack>
                <TextInput
                  label="ФИО сотрудника"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.currentTarget.value)}
                  placeholder="Иванов Иван Иванович"
                />
                <TextInput
                  label="Email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  autoComplete="email"
                />
                <PasswordInput
                  label="Пароль"
                  description="Минимум 8 символов, хотя бы одна буква и цифра"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  autoComplete="new-password"
                />
                <TextInput
                  label="Телефон"
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                />
                {error && (
                  <Text c="red" size="sm">
                    {error}
                  </Text>
                )}
                <Button type="submit" loading={submitting} fullWidth>
                  Зарегистрироваться как экспедитор
                </Button>
              </Stack>
            </form>
          )}

          <Divider my="md" />
          <Text size="sm" ta="center">
            Уже зарегистрированы?{' '}
            <Anchor component={Link} to="/login">
              Войти
            </Anchor>
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}
